import { useState } from 'react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { useJobRequestActions } from '@/hooks/useJobRequestActions'

interface ActionModalProps {
  isOpen: boolean
  action: 'review' | 'approve' | 'reject' | 'return' | 'reactivate'
  requestId: string
  onSuccess: () => void
  onCancel: () => void
}

const actionConfig = {
  review: {
    title: 'Poner en Revisión',
    buttonText: 'Poner en Revisión',
    variante: 'primario' as const,
    requireComments: false
  },
  approve: {
    title: 'Aprobar Solicitud',
    buttonText: 'Aprobar',
    variante: 'exito' as const,
    requireComments: false
  },
  reject: {
    title: 'Rechazar Solicitud',
    buttonText: 'Rechazar',
    variante: 'peligro' as const,
    requireComments: true
  },
  return: {
    title: 'Devolver Solicitud',
    buttonText: 'Devolver',
    variante: 'secundario' as const,
    requireComments: true
  },
  /**
   * El motivo no se exige (SRH-317): el caso típico es haber pulsado
   * «Rechazar» por error, y pedir una explicación para deshacerlo solo
   * invita a escribir «error» y seguir.
   */
  reactivate: {
    title: 'Reactivar Solicitud',
    buttonText: 'Reactivar',
    variante: 'primario' as const,
    requireComments: false
  }
}

export const ActionModal = ({ 
  isOpen, 
  action, 
  requestId, 
  onSuccess, 
  onCancel 
}: ActionModalProps) => {
  const [comentarios, setComentarios] = useState('')
  const [infoAdicional, setInfoAdicional] = useState('')
  const { 
    loading, 
    error, 
    reviewRequest, 
    approveRequest, 
    rejectRequest, 
    returnRequest,
    reactivateRequest,
    clearError 
  } = useJobRequestActions()
  
  const config = actionConfig[action]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (config.requireComments && !comentarios.trim()) {
      return
    }

    let result = null

    switch (action) {
      case 'review':
        result = await reviewRequest(requestId)
        break
      case 'approve':
        result = await approveRequest(requestId, 'Solicitud aprobada')
        break
      case 'reject':
        result = await rejectRequest(requestId, comentarios)
        break
      case 'return':
        result = await returnRequest(requestId, comentarios, infoAdicional || undefined)
        break
      case 'reactivate':
        result = await reactivateRequest(requestId, comentarios.trim() || undefined)
        break
    }

    if (result) {
      onSuccess()
      handleClose()
    }
  }

  const handleClose = () => {
    setComentarios('')
    setInfoAdicional('')
    clearError()
    onCancel()
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{config.title}</h3>
          <button 
            type="button" 
            className="modal-close" 
            onClick={handleClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {action === 'approve' && (
            <div className="form-group">
              <p style={{ marginBottom: '1rem', color: '#666' }}>
                ¿Está seguro que desea aprobar esta solicitud?
              </p>
            </div>
          )}

          {action !== 'approve' && (
            <div className="form-group">
              <label htmlFor="comentarios">
                {action === 'return' ? 'Comentarios de RH' : 'Comentarios'} {config.requireComments ? '(Requerido)' : '(Opcional)'}
              </label>
              <textarea 
                id="comentarios"
                value={comentarios}
                onChange={(e) => setComentarios(e.target.value)}
                required={config.requireComments}
                placeholder={action === 'return' ? 'Se devuelve - motivo y comentarios...' : 'Ingrese sus comentarios...'}
                rows={4}
                className="form-textarea"
              />
            </div>
          )}

          {action === 'return' && (
            <div className="form-group">
              <label htmlFor="infoAdicional">
                Información Adicional Solicitada (Opcional)
              </label>
              <textarea 
                id="infoAdicional"
                value={infoAdicional}
                onChange={(e) => setInfoAdicional(e.target.value)}
                placeholder="Por favor proporcione más detalles sobre..."
                rows={3}
                className="form-textarea"
              />
            </div>
          )}

          <div className="modal-actions">
            <button 
              type="button" 
              onClick={handleClose}
              className={estiloDeBoton('secundario')}
              disabled={loading}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className={estiloDeBoton(config.variante)}
              disabled={loading || (config.requireComments && !comentarios.trim())}
            >
              {loading ? 'Procesando...' : config.buttonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
